/**
 * Study Card Component
 * 
 * Individual study card with metrics, age badge, and action buttons.
 * Used within the Study Library for displaying study information.
 */

import { format, parseISO } from "date-fns";
import {
  ArrowUpRight,
  Calendar,
  Check,
  Copy,
  Download,
  Eye,
  GitCompare,
  MoreVertical,
  RefreshCw,
  Sparkles,
  Target,
  TrendingDown,
  TrendingUp,
  Trophy,
} from "lucide-react";
import { useCallback, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Progress } from "@/components/ui/progress";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { StudyLabelControl } from "@/components/study-library/StudyLabelControl";
import { formatStrictWithApprox, parseApproxConfigFromToml } from "@/components/results/utils";
import { metricThresholdsConfig } from "@/config";
import { useSaveStudyLabel } from "@/hooks/useStudyLabel";
import { toast } from "@/hooks/useToast";
import { cn } from "@/utils/cn";
import {
  type StudyWithMetadata,
  formatAge,
  getFreshnessColorClass,
  getFreshnessDescription,
} from "@/utils/studyGrouping";

// Helper to get metric color using 3-tier thresholds (red/yellow/green)
// Data-driven defaults from production analysis - can be customized in Settings
function getStudyMetricColor(value: number | null | undefined, type: 'winRate' | 'sharpe' | 'drawdown' | 'profitFactor'): string | undefined {
  if (value == null) return undefined;

  const t = metricThresholdsConfig;

  switch (type) {
    case 'winRate': {
      // Note: study.win_rate is 0-1 not 0-100, so multiply by 100
      const pct = value * 100;
      const th = t.winRate;
      if (pct >= th.green) return "text-green-400";
      if (pct >= th.yellow) return "text-yellow-400";
      return "text-red-400";
    }
    case 'sharpe': {
      const th = t.sortino;
      if (value >= th.green) return "text-green-400";
      if (value >= th.yellow) return "text-yellow-400";
      return "text-red-400";
    }
    case 'drawdown': {
      // Note: study.max_drawdown is 0-1 not 0-100, so multiply by 100
      const pct = value * 100;
      const th = t.drawdown;
      if (pct <= th.green) return "text-green-400";
      if (pct <= th.yellow) return "text-yellow-400";
      return "text-red-400";
    }
    case 'profitFactor': {
      const th = t.profitFactor;
      if (value >= th.green) return "text-green-400";
      if (value >= th.yellow) return "text-yellow-400";
      return "text-red-400";
    }
    default:
      return undefined;
  }
}

// =============================================================================
// Types
// =============================================================================

interface StudyCardProps {
  study: StudyWithMetadata;
  isSelected: boolean;
  onClick: () => void;
  onSelect: () => void;
}

// =============================================================================
// Main Component
// =============================================================================

export function StudyCard({
  study,
  isSelected,
  onClick,
  onSelect,
}: StudyCardProps) {
  const [isCopied, setIsCopied] = useState(false);

  // AIE-539: label (tag) save + known category colors for the inline editor.
  const { save: saveLabel, knownLabelColors } = useSaveStudyLabel(study.study_id);

  // Format metrics for display (AIE-319: append approx real Sortino/Sharpe)
  const bestValue = formatStrictWithApprox(
    study.best_value,
    study.total_trades,
    parseApproxConfigFromToml(study.toml_config),
  );
  const winRate = study.win_rate != null ? `${(study.win_rate * 100).toFixed(1)}%` : "—";
  const sharpe = study.sharpe_ratio?.toFixed(2) ?? "—";
  const maxDD = study.max_drawdown != null ? `${(study.max_drawdown * 100).toFixed(1)}%` : "—";
  const profitFactor = study.profit_factor?.toFixed(2) ?? "—";

  // Format date
  const createdDate = study.created_at
    ? format(parseISO(study.created_at), "MMM d, yyyy")
    : "Unknown";

  // Get freshness styling
  const freshnessClass = getFreshnessColorClass(study.freshness);
  const freshnessDesc = getFreshnessDescription(study.freshness, study.ageInDays);
  const ageDisplay = formatAge(study.ageInDays);

  // Copy study ID
  const handleCopyId = useCallback(() => {
    navigator.clipboard.writeText(study.study_id);
    setIsCopied(true);
    toast({
      title: "Copied!",
      description: "Study ID copied to clipboard",
    });
    setTimeout(() => setIsCopied(false), 2000);
  }, [study.study_id]);

  // Prevent click propagation for selection checkbox
  const handleSelectClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onSelect();
    },
    [onSelect]
  );

  // Handle card click for selection (toggle checkbox)
  const handleCardClick = useCallback(
    (e: React.MouseEvent) => {
      // Don't trigger if clicking on a button or interactive element
      const target = e.target as HTMLElement;
      if (target.closest('button') || target.closest('[role="menuitem"]')) {
        return;
      }
      onSelect();
    },
    [onSelect]
  );

  return (
    <div
      onClick={handleCardClick}
      className={cn(
        "group relative p-4 rounded-lg border transition-all cursor-pointer",
        "hover:border-dark-600/70 hover:bg-dark-800/30",
        isSelected
          ? "border-blue-500/70 bg-blue-500/5"
          : "border-dark-700/50 bg-dark-900/20"
      )}
    >
      {/* Selection checkbox */}
      <button
        onClick={handleSelectClick}
        aria-label={isSelected ? "Deselect study from comparison" : "Select study for comparison"}
        aria-pressed={isSelected}
        className={cn(
          "absolute top-3 left-3 w-5 h-5 rounded border flex items-center justify-center transition-all",
          "hover:border-blue-400",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-dark-900",
          isSelected
            ? "bg-blue-500 border-blue-500"
            : "border-dark-600 bg-transparent"
        )}
      >
        {isSelected && <Check className="h-3 w-3 text-white" />}
      </button>

      <div className="pl-7">
        {/* Header row */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            {/* Study name with version badge */}
            <div className="flex items-center gap-2 mb-1">
              <h4 className="font-bold text-lg text-white truncate tracking-tight">
                {study.study_name.replace(/^sample_/, "").toUpperCase().replace(/_/g, " ")}
              </h4>
              {study.metadata.version > 1 && (
                <Badge
                  variant="outline"
                  className="text-[10px] py-0 h-4 bg-dark-800 border-dark-700 text-dark-400"
                >
                  v{study.metadata.version}
                </Badge>
              )}
              {study.study_name.startsWith("sample_") && (
                <Badge
                  variant="outline"
                  className="text-[10px] py-0 h-4 bg-purple-500/10 border-purple-500/30 text-purple-400"
                >
                  Sample
                </Badge>
              )}
              {/* AIE-539: label icon / colored chip + inline editor */}
              <StudyLabelControl
                label={study.user_label}
                color={study.user_label_color}
                knownLabelColors={knownLabelColors}
                onSave={saveLabel}
                className="shrink-0"
              />
            </div>

            {/* Metadata row */}
            <div className="flex items-center gap-3 text-xs text-dark-500">
              <span className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {createdDate}
              </span>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span
                      className={cn(
                        "px-2 py-0.5 rounded border cursor-help text-[10px] font-bold uppercase tracking-wider",
                        freshnessClass,
                        ageDisplay === "Today" && "bg-green-500/20 text-green-400 border-green-500/40 shadow-[0_0_10px_rgba(34,197,94,0.15)] pulse-subtle"
                      )}
                    >
                      {ageDisplay}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{freshnessDesc}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <span className="text-dark-600">•</span>
                <div className="flex items-center gap-2">
                <span className="shrink-0">{study.n_trials ?? 0} trials</span>
                <div className="w-16">
                  <Progress
                    value={Math.min(((study.n_trials_completed ?? 0) / (study.n_trials ?? 1)) * 100, 100)}
                    variant={study.status === 'completed' ? 'success' : study.status === 'failed' ? 'danger' : 'brand'}
                    className="h-1"
                    animate={false}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Actions menu */}
          <div className="flex items-center gap-1">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={(e) => e.stopPropagation()}
                >
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                <DropdownMenuItem onClick={onClick}>
                  <Eye className="h-4 w-4 mr-2" />
                  View Details
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleSelectClick}>
                  <GitCompare className="h-4 w-4 mr-2" />
                  {isSelected ? "Deselect" : "Select for Compare"}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleCopyId}>
                  {isCopied ? (
                    <Check className="h-4 w-4 mr-2 text-green-400" />
                  ) : (
                    <Copy className="h-4 w-4 mr-2" />
                  )}
                  Copy Study ID
                </DropdownMenuItem>
                <DropdownMenuItem disabled>
                  <Download className="h-4 w-4 mr-2" />
                  Export Config
                </DropdownMenuItem>
                <DropdownMenuItem disabled>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Re-run Study
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={(e) => {
                e.stopPropagation();
                onClick();
              }}
            >
              <ArrowUpRight className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Metrics row */}
        <div className="grid grid-cols-5 gap-3">
          <MetricItem
            icon={<Trophy className="h-3.5 w-3.5 text-yellow-500" />}
            label="Best"
            value={bestValue}
            highlight
          />
          <MetricItem
            icon={<Target className="h-3.5 w-3.5 text-green-500" />}
            label="Win Rate"
            value={winRate}
            valueColor={getStudyMetricColor(study.win_rate, 'winRate')}
          />
          <MetricItem
            icon={<Sparkles className="h-3.5 w-3.5 text-blue-500" />}
            label="Score"
            value={sharpe}
            valueColor={getStudyMetricColor(study.sharpe_ratio, 'sharpe')}
          />
          <MetricItem
            icon={<TrendingDown className="h-3.5 w-3.5 text-red-500" />}
            label="Max DD"
            value={maxDD}
            valueColor={getStudyMetricColor(study.max_drawdown, 'drawdown')}
          />
          <MetricItem
            icon={<TrendingUp className="h-3.5 w-3.5 text-purple-500" />}
            label="PF"
            value={profitFactor}
            valueColor={getStudyMetricColor(study.profit_factor, 'profitFactor')}
          />
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Metric Item Component
// =============================================================================

interface MetricItemProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  highlight?: boolean;
  valueColor?: string | undefined;
}

function MetricItem({ icon, label, value, highlight, valueColor }: MetricItemProps) {
  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-1 text-xs text-dark-500 mb-0.5">
        {icon}
        <span>{label}</span>
      </div>
      <span
        className={cn(
          "text-sm font-medium",
          highlight ? "text-yellow-400" : valueColor || "text-dark-200"
        )}
      >
        {value}
      </span>
    </div>
  );
}

export default StudyCard;

